import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageHero from "@/components/base/PageHero";
import EpisodeCard from "@/components/base/EpisodeCard";
import CharacterCard from "@/components/base/CharacterCard";
import NewsCard from "@/components/base/NewsCard";
import { episodes } from "@/mocks/episodes";
import { characters } from "@/mocks/characters";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

type TabKey = "all" | "episode" | "character" | "news";

const tabs: { key: TabKey; label: string }[] = [
  { key: "all", label: "全部结果" },
  { key: "episode", label: "剧集" },
  { key: "character", label: "角色" },
  { key: "news", label: "资讯" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<TabKey>("all");

  useSeo({
    title: keyword
      ? `“${keyword}”的搜索结果 - 年轻的母亲2`
      : "全站搜索 - 年轻的母亲2",
    description: keyword
      ? `在《年轻的母亲2》内容站中搜索“${keyword}”，查看相关的剧集、角色与资讯结果。`
      : "搜索《年轻的母亲2》的剧集、角色与资讯内容，快速定位你需要的剧情信息。",
    keywords: "年轻的母亲2搜索,站内搜索,剧集查询,角色查询,资讯查询",
  });

  const q = keyword.toLowerCase();

  const episodeResults = useMemo(
    () =>
      q
        ? episodes.filter(
            (ep) =>
              ep.title.toLowerCase().includes(q) ||
              ep.summary.toLowerCase().includes(q) ||
              ep.content.toLowerCase().includes(q) ||
              ep.cast.toLowerCase().includes(q) ||
              ep.tags.join(" ").toLowerCase().includes(q) ||
              `第${ep.number}集`.includes(keyword),
          )
        : [],
    [q, keyword],
  );

  const characterResults = useMemo(
    () =>
      q
        ? characters.filter(
            (ch) =>
              ch.name.toLowerCase().includes(q) ||
              ch.actor.toLowerCase().includes(q) ||
              ch.intro.toLowerCase().includes(q) ||
              ch.background.toLowerCase().includes(q) ||
              ch.tagline.toLowerCase().includes(q),
          )
        : [],
    [q],
  );

  const newsResults = useMemo(
    () =>
      q
        ? newsList.filter(
            (n) =>
              n.title.toLowerCase().includes(q) ||
              n.summary.toLowerCase().includes(q) ||
              n.category.toLowerCase().includes(q) ||
              n.content.join(" ").toLowerCase().includes(q),
          )
        : [],
    [q],
  );

  const totalCount = episodeResults.length + characterResults.length + newsResults.length;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next = input.trim();
    setSearchParams(next ? { q: next } : {});
    setTab("all");
  };

  const showEpisode = tab === "all" || tab === "episode";
  const showCharacter = tab === "all" || tab === "character";
  const showNews = tab === "all" || tab === "news";

  const countOf = (key: TabKey) => {
    switch (key) {
      case "episode":
        return episodeResults.length;
      case "character":
        return characterResults.length;
      case "news":
        return newsResults.length;
      default:
        return totalCount;
    }
  };

  const hotEpisodes = episodes.slice(0, 3);
  const hotCharacters = characters.slice(0, 4);
  const hotNews = newsList.slice(0, 2);

  const activeCount = countOf(tab);

  return (
    <div className="w-full">
      <PageHero
        eyebrow="全站搜索"
        title={keyword ? `“${keyword}”的搜索结果` : "全站搜索"}
        desc="覆盖剧集、角色、资讯三大内容模块，输入关键词即可快速定位你想了解的内容。"
        breadcrumb={[{ label: "首页", to: "/" }, { label: "搜索结果" }]}
      >
        <form onSubmit={handleSubmit} className="flex w-full max-w-2xl flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              type="search"
              placeholder="搜索剧集、角色、资讯"
              aria-label="站内搜索"
              className="h-12 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-4 font-label text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <button
            type="submit"
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-search-line text-base"></i>
            </span>
            搜索
          </button>
        </form>
      </PageHero>

      <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
        {keyword && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 font-label text-sm transition-colors ${
                    tab === t.key
                      ? "bg-primary-500 font-semibold text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  {t.label}
                  <span
                    className={`rounded-full px-1.5 text-xs ${
                      tab === t.key ? "bg-background-50/20 text-background-50" : "bg-background-200 text-foreground-600"
                    }`}
                  >
                    {countOf(t.key)}
                  </span>
                </button>
              ))}
            </div>

            <p className="mt-4 text-sm text-foreground-600">
              共找到 <span className="font-semibold text-primary-600">{totalCount}</span> 条与
              “{keyword}”相关的结果
            </p>

            {totalCount === 0 && (
              <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 p-10 text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-3xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  没有找到相关内容
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  试试更换关键词，或从下方热门内容开始浏览。
                </p>
              </div>
            )}

            {totalCount > 0 && activeCount === 0 && (
              <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 p-10 text-center">
                <p className="text-sm text-foreground-600">
                  当前分类下没有与“{keyword}”相关的结果，试试切换到其他分类。
                </p>
              </div>
            )}

            {totalCount > 0 && (
              <div className="mt-8 flex flex-col gap-10">
                {showEpisode && episodeResults.length > 0 && (
                  <section>
                    <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <span className="h-5 w-1 rounded-full bg-primary-500"></span>
                      剧集（{episodeResults.length}）
                    </h2>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                      {episodeResults.map((ep) => (
                        <EpisodeCard key={ep.id} {...ep} />
                      ))}
                    </div>
                  </section>
                )}

                {showCharacter && characterResults.length > 0 && (
                  <section>
                    <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <span className="h-5 w-1 rounded-full bg-accent-500"></span>
                      角色（{characterResults.length}）
                    </h2>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                      {characterResults.map((ch) => (
                        <CharacterCard key={ch.id} {...ch} />
                      ))}
                    </div>
                  </section>
                )}

                {showNews && newsResults.length > 0 && (
                  <section>
                    <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <span className="h-5 w-1 rounded-full bg-secondary-500"></span>
                      资讯（{newsResults.length}）
                    </h2>
                    <div className="flex flex-col gap-4">
                      {newsResults.map((item) => (
                        <NewsCard key={item.id} {...item} />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </>
        )}

        {!keyword && (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-10 text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-line text-3xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
              输入关键词开始搜索
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              例如“婆媳”“林慧”“结局”，或直接从下方热门内容开始。
            </p>
          </div>
        )}

        {/* 热门内容入口 */}
        <section className="mt-12">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
            <span className="flex h-5 w-5 items-center justify-center text-accent-500">
              <i className="ri-fire-line text-lg"></i>
            </span>
            热门内容
          </h2>

          <div className="flex flex-col gap-8">
            <div>
              <h3 className="mb-3 font-heading text-sm font-bold text-foreground-700">热门剧集</h3>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {hotEpisodes.map((ep) => (
                  <EpisodeCard key={ep.id} {...ep} />
                ))}
              </div>
            </div>

            <div>
              <h3 className="mb-3 font-heading text-sm font-bold text-foreground-700">热门角色</h3>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {hotCharacters.map((ch) => (
                  <CharacterCard key={ch.id} {...ch} />
                ))}
              </div>
            </div>

            <div>
              <h3 className="mb-3 font-heading text-sm font-bold text-foreground-700">热门资讯</h3>
              <div className="flex flex-col gap-4">
                {hotNews.map((item) => (
                  <NewsCard key={item.id} {...item} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="mt-10 flex justify-center">
          <Link
            to="/episodes"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 font-label text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-play-list-2-line text-base"></i>
            </span>
            浏览全部剧集
          </Link>
        </div>
      </div>
    </div>
  );
}